import { TCommunicationsCall } from "../../../../../services/api-communications";
import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";

const ACTIVE_CALL_STATUSES = new Set([
  "queued",
  "initiated",
  "ringing",
  "in-progress",
  "in_progress",
  "answered",
]);

const TERMINAL_CALL_STATUSES = new Set([
  "completed",
  "failed",
  "busy",
  "no_answer",
  "no-answer",
  "canceled",
  "cancelled",
]);

export const PENDING_HANGUP_STATUSES = new Set([
  "pending",
  "provider_accepted",
]);

export const normalizeCallStatus = (
  status: string | null | undefined,
): string => (status || "").trim().toLowerCase();

export const isCallActive = (status: string | null | undefined): boolean => {
  return ACTIVE_CALL_STATUSES.has(normalizeCallStatus(status));
};

export const isCallTerminal = (status: string | null | undefined): boolean => {
  return TERMINAL_CALL_STATUSES.has(normalizeCallStatus(status));
};

export const shouldPollCall = (call: TCommunicationsCall | null): boolean => {
  if (!call || isCallTerminal(call.status)) {
    return false;
  }

  if (isCallActive(call.status)) {
    return true;
  }

  const normalizedHangupStatus = (call.hangup_request_status || "")
    .trim()
    .toLowerCase();
  return PENDING_HANGUP_STATUSES.has(normalizedHangupStatus);
};

export const formatDateTime = (value?: string | null): string => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

export const formatDuration = (durationSeconds?: number | null): string => {
  if (typeof durationSeconds !== "number") return "-";
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export const formatCurrencyFromCents = (
  cents: number | null | undefined,
  currency: string | null | undefined,
): string => {
  if (typeof cents !== "number" || !Number.isFinite(cents)) {
    return "-";
  }
  const normalizedCurrency = (currency || "USD").trim().toUpperCase() || "USD";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: normalizedCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
};

export const formatDateInputValue = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const toIsoDateOrNull = (value: string): string | null => {
  const normalizedValue = (value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
    return null;
  }
  return normalizedValue;
};

export const toDateInputFromPeriodValue = (
  value: string | null | undefined,
): string | null => {
  const normalizedValue = (value || "").trim();
  if (!normalizedValue) {
    return null;
  }

  const explicitDateMatch = normalizedValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (explicitDateMatch?.[1]) {
    return explicitDateMatch[1];
  }

  const parsedDate = new Date(normalizedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return formatDateInputValue(parsedDate);
};

export const formatInteger = (value: number | null | undefined): string => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "0";
  }
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatDurationLong = (durationSeconds?: number | null): string => {
  if (
    typeof durationSeconds !== "number" ||
    !Number.isFinite(durationSeconds)
  ) {
    return "-";
  }

  const hours = Math.floor(durationSeconds / 3600);
  const minutes = Math.floor((durationSeconds % 3600) / 60);
  const seconds = durationSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

export const resolvePhoneNumberCapabilities = (
  capabilities: string[] | Record<string, boolean> | null | undefined,
): string[] => {
  if (!capabilities) {
    return [];
  }

  if (Array.isArray(capabilities)) {
    return capabilities
      .map((item) => String(item || "").trim())
      .filter((item) => !!item);
  }

  if (typeof capabilities === "object") {
    return Object.keys(capabilities).filter(
      (capability) => capabilities[capability],
    );
  }

  return [];
};

export const formatPhoneCapabilities = (
  capabilities: string[] | Record<string, boolean> | null | undefined,
): string => {
  const resolvedCapabilities = resolvePhoneNumberCapabilities(capabilities);
  if (!resolvedCapabilities.length) {
    return "N/A";
  }
  return resolvedCapabilities.join(", ");
};

export const getCallStatusBadgeStyle = (status?: string | null) => {
  const normalized = normalizeCallStatus(status);

  if (["completed"].includes(normalized)) {
    return {
      backgroundColor: ADMIN_TOKENS.color.surfaceAccent,
      borderColor: ADMIN_TOKENS.color.borderAccent,
      textColor: ADMIN_TOKENS.color.accentDark,
    };
  }

  if (
    [
      "failed",
      "busy",
      "no-answer",
      "no_answer",
      "canceled",
      "cancelled",
    ].includes(normalized)
  ) {
    return {
      backgroundColor: ADMIN_TOKENS.color.errorSurface,
      borderColor: ADMIN_TOKENS.color.errorBorder,
      textColor: ADMIN_TOKENS.color.errorTextStrong,
    };
  }

  return {
    backgroundColor: ADMIN_TOKENS.color.surfaceMuted,
    borderColor: ADMIN_TOKENS.color.borderMuted,
    textColor: ADMIN_TOKENS.color.textSecondary,
  };
};

export const getMessageStatusBadgeStyle = (status?: string | null) => {
  const normalized = normalizeCallStatus(status);

  if (["delivered", "sent", "read"].includes(normalized)) {
    return {
      backgroundColor: ADMIN_TOKENS.color.surfaceAccent,
      borderColor: ADMIN_TOKENS.color.borderAccent,
      textColor: ADMIN_TOKENS.color.accentDark,
    };
  }

  if (["failed", "undelivered"].includes(normalized)) {
    return {
      backgroundColor: ADMIN_TOKENS.color.errorSurface,
      borderColor: ADMIN_TOKENS.color.errorBorder,
      textColor: ADMIN_TOKENS.color.errorTextStrong,
    };
  }

  return {
    backgroundColor: ADMIN_TOKENS.color.surfaceMuted,
    borderColor: ADMIN_TOKENS.color.borderMuted,
    textColor: ADMIN_TOKENS.color.textSecondary,
  };
};
